import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import { Modal, Pressable, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeIn, ZoomIn } from 'react-native-reanimated';

import { AppText } from '@/components/ui/AppText';
import { Button } from '@/components/ui/Button';
import { useThemePalette } from '@/config/theme';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';

const CYCLE = 14;

/** One day pill in the 14-day track. */
function DayPill({ day, done, isToday }: { day: number; done: boolean; isToday: boolean }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  return (
    <View
      className="items-center justify-center rounded-full"
      style={{
        width: 34,
        height: 34,
        backgroundColor: done ? palette.voltBright : palette.inset,
        borderWidth: isToday ? 2 : 0,
        borderColor: palette.voltBright,
      }}
    >
      <AppText
        variant="micro"
        weight="bold"
        tabular
        style={{ color: done ? '#06210F' : isToday ? palette.voltBright : palette.muted, fontSize: 11 }}
      >
        {formatNumber(day, numeralOptions)}
      </AppText>
    </View>
  );
}

/**
 * Daily login popup: a 14-day streak track with the day being claimed
 * highlighted. Shown once per day on app open (gated by the manager).
 */
export function DailyStreakModal({
  visible,
  claimDay,
  filledBefore,
  reward,
  loading,
  onClaim,
  onClose,
}: {
  visible: boolean;
  /** The day in the 1..14 cycle being claimed today. */
  claimDay: number;
  /** Days already completed earlier in this cycle (0..13). */
  filledBefore: number;
  reward: number;
  loading: boolean;
  onClaim: () => void;
  onClose: () => void;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const days = Array.from({ length: CYCLE }, (_, i) => i + 1);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Animated.View
        entering={FadeIn.duration(180)}
        className="flex-1 items-center justify-center px-6"
        style={{ backgroundColor: 'rgba(0,0,0,0.66)' }}
      >
        <Pressable className="absolute inset-0" accessibilityRole="button" onPress={onClose} />
        <Animated.View
          entering={ZoomIn.springify().damping(16)}
          className="w-full rounded-3xl border border-volt-line bg-surface-2 px-5 pb-5 pt-6"
          style={{ maxWidth: 360 }}
        >
          <View className="items-center">
            <View className="h-16 w-16 items-center justify-center rounded-full bg-volt-glass-strong">
              <Ionicons name="flame" size={30} color={palette.captain} />
            </View>
            <AppText variant="micro" className="mt-3 uppercase tracking-widest text-muted">
              {t('rewards.streakModal.title')}
            </AppText>
            <AppText variant="display" weight="bold" className="text-volt-bright">
              {t('rewards.streakModal.dayN', { day: formatNumber(claimDay, numeralOptions) })}
            </AppText>
            <AppText variant="caption" className="mt-1 text-center text-soft">
              {t('rewards.streakModal.subtitle')}
            </AppText>
          </View>

          {/* 14-day track, two rows of seven. */}
          <View className="mt-5 gap-2.5">
            {[days.slice(0, 7), days.slice(7)].map((row, ri) => (
              <View key={ri} className="flex-row justify-between">
                {row.map((day) => (
                  <DayPill
                    key={day}
                    day={day}
                    done={day <= filledBefore}
                    isToday={day === claimDay}
                  />
                ))}
              </View>
            ))}
          </View>

          <View className="mt-5 flex-row items-center justify-center gap-1.5">
            <Ionicons name="server" size={16} color={palette.voltBright} />
            <AppText variant="title" weight="bold" tabular className="text-volt-bright">
              {`+${formatNumber(reward, numeralOptions)}`}
            </AppText>
            <AppText variant="caption" className="text-muted">
              {t('rewards.coins')}
            </AppText>
          </View>

          <View className="mt-4">
            <Button label={t('rewards.streakModal.claim')} variant="primary" loading={loading} onPress={onClaim} />
          </View>
          <Pressable accessibilityRole="button" onPress={onClose} className="mt-2 items-center py-2">
            <AppText variant="micro" className="text-muted">
              {t('rewards.streakModal.later')}
            </AppText>
          </Pressable>
        </Animated.View>
      </Animated.View>
    </Modal>
  );
}
